import { ReactNode, RefObject } from 'react';
import {
  AriaTextFieldOptions,
  TextFieldAria,
  useTextField,
} from '@react-aria/textfield';
import {
  ValueBasePropsKeys,
  InputBaseKeys,
  ValidationIsRequiredKey,
  FocusablePropsKeys,
  TextInputDOMPropsKeys,
} from '../../utils/types';

interface CharacterCounter {
  max: number;
  current: number;
}

export interface SapphireTextFieldProps<T extends 'input' | 'textarea'>
  extends Pick<
    AriaTextFieldOptions<T>,
    | ValueBasePropsKeys
    | TextInputDOMPropsKeys
    | FocusablePropsKeys
    | InputBaseKeys
    | ValidationIsRequiredKey
    | 'label'
    | 'inputElementType'
    | 'autoFocus'
    | 'autoCapitalize'
  > {
  /**
   * Whether the input should render as having an error and an error message.
   * This also sets the appropriate `aria` attributes on the input.
   *
   * A `boolean` sets the error state. Can be paired with the "note" prop with Field.Note variant="error"
   * A `ReactNode` sets the error state with an additional error message.
   *
   * **An error message is strongly recommended because an error should always have an explanation about how to fix it.**
   */
  error?: boolean | ReactNode;
  /**
   * A note to show below the input.
   * If the input has an error message, this note will be replaced by that.
   * Use {@Link Field.Note} as a content wrapper.
   */
  note?: ReactNode;
  /**
   * Shows the character counter with the provided max and current values.
   */
  characterCounter?: CharacterCounter;
  /**
   * A HelpButton to render next to the label.
   */
  contextualHelp?: ReactNode;
  /**
   * To visually indicate if this field is required or optional.
   * @default false
   */
  necessityIndicator?: boolean;
}

/**
 * This hook is just a thin wrapper around react-aria's `useTextField`.
 * We use this to map some of our text field props to what they expect
 * in order to correctly specify the error state and message of a field.
 */
export const useSapphireTextField = <T extends 'input' | 'textarea'>(
  { error, note, ...textFieldProps }: SapphireTextFieldProps<T>,
  ref: RefObject<{ input: HTMLInputElement; textarea: HTMLTextAreaElement }[T]>
): TextFieldAria<T> => {
  const textFieldAria = useTextField(
    {
      ...textFieldProps,
      isInvalid: error ? true : undefined,
    },
    ref
  );

  /**
   * Patch to fix incorrect props passing from useTextField after upgrade to v.3.13.0
   */

  if (textFieldProps.value === undefined || textFieldProps.defaultValue) {
    textFieldAria.inputProps.value = undefined;
  }

  if (textFieldProps.defaultValue) {
    textFieldAria.inputProps.defaultValue = textFieldProps.defaultValue;
  }

  return textFieldAria;
};
